// NodeTypeWriteService.js

import { collection, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import { generateKey } from 'utilities/Keys';
import { pluralizeTitle } from 'utilities/Languages';
import { ViewType } from 'utilities/NodeType';
import { createWorkspaceType } from 'services/api/ApiAccessService';
import { getWorkspaceDatabaseByKey } from 'services/databases/DatabaseReadService';
import { createDatabase } from 'services/databases/DatabaseWriteService';
import { getOrCreateNodeTypeDashboard } from 'services/dashboards/DashboardWriteService';
import { canWorkspaceRole, getWorkspaceRoleForUser } from 'services/workspaces/WorkspaceRoleService';

const normalizeLabel = (label) => String(label || '').trim();
const normalizeLabelLower = (label) => normalizeLabel(label).toLowerCase();
const ALLOWED_DEFAULT_VIEW_TYPES = new Set(Object.values(ViewType));
const DEFAULT_FALLBACK_VIEW_TYPE = ViewType.LIST;
const buildDefaultDatabaseKey = (workspaceKey, typeKey) => (
  `${workspaceKey}_${typeKey}_all`.replace(/\//g, '_')
);

const normalizeDefaultView = (value, fallback = DEFAULT_FALLBACK_VIEW_TYPE) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (ALLOWED_DEFAULT_VIEW_TYPES.has(normalized)) return normalized;
  return ALLOWED_DEFAULT_VIEW_TYPES.has(fallback) ? fallback : DEFAULT_FALLBACK_VIEW_TYPE;
};

async function initializeViewsForNewType({
  userKey,
  workspaceKey,
  typeKey,
  labelPlural,
}) {
  if (!userKey || !workspaceKey || !typeKey) return;

  const databaseKey = buildDefaultDatabaseKey(workspaceKey, typeKey);
  const databaseTitle = normalizeLabel(labelPlural) || 'Items';

  const existingDatabase = await getWorkspaceDatabaseByKey(workspaceKey, databaseKey);

  if (!existingDatabase) {
    await createDatabase({
      key: databaseKey,
      workspaceKey,
      userKey,
      database: {
        key: databaseKey,
        type: typeKey,
        title: databaseTitle,
        filters: [],
      },
    });
  }
}

export async function ensureTypeDatabaseScaffold({
  userKey,
  workspaceKey,
  typeKey,
  labelPlural,
} = {}) {
  await initializeViewsForNewType({
    userKey,
    workspaceKey,
    typeKey,
    labelPlural,
  });
}

function getWorkspaceTypesDatabase(workspaceKey) {
  if (!workspaceKey) return null;
  return collection(db, collections.workspaces, workspaceKey, collections.nodetypes);
}

function getRootTypesDatabase() {
  return collection(db, collections.nodetypes);
}

function getFirebaseErrorDetails(error) {
  return {
    code: error?.code || null,
    message: error?.message || 'Unknown error',
  };
}

export async function workspaceNodeTypeExists({ workspaceKey, labelLower }) {
  const normalized = normalizeLabelLower(labelLower);
  const typesDatabase = getWorkspaceTypesDatabase(workspaceKey);
  if (!typesDatabase || !normalized) return false;

  const q = query(typesDatabase, where('labelLower', '==', normalized), limit(1));
  const snap = await getDocs(q);
  return !snap.empty;
}

async function nodeTypeNameConflict({ workspaceKey, label, labelPlural }) {
  const collectionsToCheck = [
    getWorkspaceTypesDatabase(workspaceKey),
    getRootTypesDatabase(),
  ].filter(Boolean);

  if (!collectionsToCheck.length) return null;

  const normalizedLabel = normalizeLabelLower(label);
  const normalizedPlural = normalizeLabelLower(labelPlural);
  if (!normalizedLabel && !normalizedPlural) return null;

  const namesToCheck = new Set([normalizedLabel, normalizedPlural].filter(Boolean));
  if (!namesToCheck.size) return null;

  const names = Array.from(namesToCheck);

  const hasConflictInDatabase = async (typesDatabase) => {
    const checks = [];

    names.forEach((name) => {
      checks.push(getDocs(query(typesDatabase, where('labelLower', '==', name), limit(1))));
      checks.push(getDocs(query(typesDatabase, where('labelPluralLower', '==', name), limit(1))));
    });

    const snapshots = await Promise.all(checks);
    return snapshots.some((snap) => !snap.empty);
  };

  const conflictChecks = await Promise.all(
    collectionsToCheck.map((typesDatabase) => hasConflictInDatabase(typesDatabase))
  );

  const hasConflict = conflictChecks.some(Boolean);

  if (hasConflict) {
    return 'A type with that label already exists in your available type set.';
  }

  return null;
}

export async function createWorkspaceNodeType({
  label,
  labelPlural,
  description,
  iconKey,
  defaultView,
  userKey,
  workspaceKey,
  anchorNodeKey,
} = {}) {
  const cleanedLabel = normalizeLabel(label);
  if (!cleanedLabel) {
    throw new Error('Please enter a label.');
  }

  if (!workspaceKey) {
    throw new Error('No space selected.');
  }

  if (!userKey) {
    throw new Error('You must be logged in.');
  }

  const actorRole = await getWorkspaceRoleForUser({
    workspaceKey,
    userKey,
  });

  if (!canWorkspaceRole(actorRole, 'types.create')) {
    throw new Error('You do not have permission to create types in this workspace.');
  }

  const labelLower = normalizeLabelLower(cleanedLabel);
  const cleanedPlural = normalizeLabel(labelPlural) || pluralizeTitle(cleanedLabel);
  const labelPluralLower = normalizeLabelLower(cleanedPlural);
  const cleanedDescription = normalizeLabel(description);
  const cleanedIconKey = normalizeLabel(iconKey);
  const cleanedDefaultView = normalizeDefaultView(defaultView, DEFAULT_FALLBACK_VIEW_TYPE);
  const conflictMessage = await nodeTypeNameConflict({
    workspaceKey,
    label: cleanedLabel,
    labelPlural: cleanedPlural,
  });
  if (conflictMessage) {
    throw new Error(conflictMessage);
  }

  let key = generateKey();
  const now = Timestamp.now();

  const payload = {
    key,
    userKey,
    workspaceKey,
    label: cleanedLabel,
    labelLower,
    labelPlural: cleanedPlural,
    labelPluralLower,
    description: cleanedDescription,
    aliases: [],
    aliasesUpper: [],
    allowedParents: [],
    autoFocusOnAdd: true,
    autosizeHighlight: true,
    defaultView: cleanedDefaultView,
    iconKey: cleanedIconKey || 'deployed_code',
    icon: '',
    photoUrl: null,
    prioritySort: [],
    schema: { fields: [] },
    showHighlight: false,
    suggestedChildren: [],
    version: 1,
    dateCreated: now,
    dateModified: now,
  };

  try {
    const result = await createWorkspaceType({
      workspaceId: workspaceKey,
      label: cleanedLabel,
      labelPlural: cleanedPlural,
      description: cleanedDescription,
      iconKey: cleanedIconKey || 'deployed_code',
      defaultView: cleanedDefaultView,
      schema: { fields: [] },
    });

    if (result?.id) {
      key = result.id;
      payload.key = result.id;
    }
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage typeDocWrite (${details.code || 'no-code'}): ${details.message}`);
  }

  try {
    await initializeViewsForNewType({
      userKey,
      workspaceKey,
      typeKey: key,
      labelPlural: cleanedPlural,
      defaultView: cleanedDefaultView,
      anchorNodeKey,
    });
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage initializeViewsForNewType (${details.code || 'no-code'}): ${details.message}`);
  }

  try {
    await getOrCreateNodeTypeDashboard({
      workspaceKey,
      typeKey: key,
      userKey,
      title: 'Home',
      columnCount: 3,
    });
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage getOrCreateNodeTypeDashboard (${details.code || 'no-code'}): ${details.message}`);
  }

  return payload;
}
